// Spa & Wellness feature content for hotels that operate a full spa.
// Each entry powers <SpaWellnessFeature/> on the corresponding hotel detail page.
// Content is trilingual ({en, fr, es}). Prices already shown in spa_menu are reused
// via the component (top 4 items are surfaced automatically as "Offerings").

const SPA_FEATURES = {
  // ───────────────────────────────────────────────────────────────────────────
  "kasbah-hotel-xaluca-arfoud": {
    image:
      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=2000&q=80",
    signature_label: {
      en: "1,200 m² wellness sanctuary",
      fr: "Sanctuaire bien-être de 1 200 m²",
      es: "Santuario de bienestar de 1 200 m²",
    },
    title: {
      en: "A sanctuary of",
      fr: "Un sanctuaire de",
      es: "Un santuario de",
    },
    title_accent: {
      en: "argan & steam.",
      fr: "vapeur & d'argan.",
      es: "vapor y argán.",
    },
    intro: {
      en: "At the heart of the kasbah, a 1,200 m² spa wraps you in eucalyptus steam, the warm crackle of beldi soap and the slow rhythm of argan-oil rituals carried by Berber families for generations. Ten private cabins, a contrast circuit and a candle-lit relaxation lounge open onto the cactus garden.",
      fr: "Au cœur de la kasbah, un spa de 1 200 m² vous enveloppe de vapeur d'eucalyptus, du crépitement du savon beldi et du rythme lent des rituels à l'huile d'argan transmis par les familles berbères. Dix cabines privatives, un parcours contrasté et un salon de relaxation aux chandelles ouvrent sur le jardin de cactus.",
      es: "En el corazón de la kasbah, un spa de 1 200 m² lo envuelve en vapor de eucalipto, el chasquido del jabón beldi y el ritmo lento de los rituales con aceite de argán transmitidos por familias bereberes. Diez cabinas privadas, un circuito de contraste y un salón de relajación a la luz de las velas se abren al jardín de cactus.",
    },
    stats: [
      { value: { en: "1 200 m²", fr: "1 200 m²", es: "1 200 m²" }, label: { en: "Spa surface", fr: "Surface du spa", es: "Superficie spa" } },
      { value: { en: "10", fr: "10", es: "10" }, label: { en: "Private cabins", fr: "Cabines privatives", es: "Cabinas privadas" } },
      { value: { en: "10h – 21h", fr: "10h – 21h", es: "10h – 21h" }, label: { en: "Daily hours", fr: "Tous les jours", es: "Todos los días" } },
    ],
    experiences: [
      {
        title: { en: "Traditional Berber hammam", fr: "Hammam berbère traditionnel", es: "Hammam bereber tradicional" },
        desc: {
          en: "Steam, beldi black-soap scrub and a rhassoul-clay envelopment — the gateway ritual.",
          fr: "Vapeur, gommage au savon noir beldi et enveloppement au rhassoul — le rituel d'entrée.",
          es: "Vapor, exfoliación con jabón negro beldi y envoltura de rhassoul — el ritual de entrada.",
        },
      },
      {
        title: { en: "Vichy rain showers", fr: "Douches pluie Vichy", es: "Duchas lluvia Vichy" },
        desc: {
          en: "Five horizontal jets cascade over the body for full-length argan-oil rituals.",
          fr: "Cinq jets horizontaux ruissellent sur le corps pour des rituels d'argan de longue durée.",
          es: "Cinco chorros horizontales caen sobre el cuerpo para rituales con argán de larga duración.",
        },
      },
      {
        title: { en: "Couples' & pre-bridal", fr: "Soin en duo & pré-mariage", es: "Tratamiento en pareja y pre-boda" },
        desc: {
          en: "A private suite for two, four hands, candles, mint tea — a full afternoon together.",
          fr: "Une suite privée pour deux, quatre mains, des bougies, du thé à la menthe — un après-midi entier.",
          es: "Una suite privada para dos, cuatro manos, velas, té con menta — toda una tarde juntos.",
        },
      },
    ],
    spaces: [
      {
        name: { en: "Steam hammam", fr: "Hammam à vapeur", es: "Hammam de vapor" },
        detail: {
          en: "Traditional zellige hammam scented with eucalyptus — heated benches in three temperatures.",
          fr: "Hammam traditionnel en zellige parfumé à l'eucalyptus — banquettes chauffées à trois températures.",
          es: "Hammam tradicional de zellige perfumado con eucalipto — bancos calefactados en tres temperaturas.",
        },
      },
      {
        name: { en: "Dry Finnish sauna", fr: "Sauna finlandais sec", es: "Sauna finlandesa seca" },
        detail: {
          en: "Aspen-wood cabin with herbal infusions — for contrast therapy with the ice machine.",
          fr: "Cabine en bois de tremble et infusions d'herbes — pour la thérapie de contraste avec la machine à glace.",
          es: "Cabina de madera de álamo e infusiones de hierbas — para terapia de contraste con la máquina de hielo.",
        },
      },
      {
        name: { en: "Candle-lit relaxation lounge", fr: "Salon de relaxation aux chandelles", es: "Salón de relajación a la luz de las velas" },
        detail: {
          en: "Heated daybeds, mint tea, dates and roasted almonds — open onto the cactus garden.",
          fr: "Daybeds chauffés, thé à la menthe, dattes et amandes grillées — ouvert sur le jardin de cactus.",
          es: "Daybeds calefactados, té con menta, dátiles y almendras tostadas — abierto al jardín de cactus.",
        },
      },
    ],
    cta_to: "/contact?topic=spa-arfoud",
  },

  // ───────────────────────────────────────────────────────────────────────────
  "kasbah-hotel-tombouctou": {
    image:
      "https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&w=2000&q=80",
    signature_label: {
      en: "Saharian wellness",
      fr: "Bien-être saharien",
      es: "Bienestar sahariano",
    },
    title: {
      en: "Spa & wellness in",
      fr: "Spa & bien-être au",
      es: "Spa y bienestar en",
    },
    title_accent: {
      en: "the tones of the Sahara.",
      fr: "ton du Sahara.",
      es: "tonos del Sáhara.",
    },
    intro: {
      en: "Tombouctou's spa wing borrows the warm ochres of the dunes — a quiet circuit of hammam, sauna and Jacuzzi where therapists work with cold-pressed argan, beldi soap and rose water from the Atlas. The sound that travels here is the rustle of palms.",
      fr: "L'aile spa de Tombouctou emprunte les ocres chaudes des dunes — un circuit calme de hammam, sauna et Jacuzzi où nos thérapeutes travaillent l'argan pressé à froid, le savon beldi et l'eau de rose de l'Atlas. Le son qui voyage ici, c'est le bruissement des palmes.",
      es: "El ala spa de Tombouctou toma los ocres cálidos de las dunas — un circuito tranquilo de hammam, sauna y Jacuzzi donde los terapeutas trabajan con argán prensado en frío, jabón beldi y agua de rosas del Atlas. El sonido que viaja aquí es el susurro de las palmeras.",
    },
    stats: [
      { value: { en: "4", fr: "4", es: "4" }, label: { en: "Treatment cabins", fr: "Cabines de soin", es: "Cabinas de tratamiento" } },
      { value: { en: "2", fr: "2", es: "2" }, label: { en: "Hot/cold circuits", fr: "Circuits chaud/froid", es: "Circuitos frío/calor" } },
      { value: { en: "5", fr: "5", es: "5" }, label: { en: "Wellness rituals", fr: "Rituels bien-être", es: "Rituales de bienestar" } },
    ],
    experiences: [
      {
        title: { en: "Desert recovery hammam", fr: "Hammam récupération du désert", es: "Hammam recuperación del desierto" },
        desc: {
          en: "After the dunes — steam, beldi-soap scrub and a long argan-oil massage to put the body back in order.",
          fr: "Après les dunes — vapeur, gommage au savon beldi et long massage à l'argan pour remettre le corps en ordre.",
          es: "Tras las dunas — vapor, exfoliación con jabón beldi y un largo masaje con argán para reordenar el cuerpo.",
        },
      },
      {
        title: { en: "Jacuzzi sequence", fr: "Séquence Jacuzzi", es: "Secuencia Jacuzzi" },
        desc: {
          en: "Hot Jacuzzi, dry sauna, cool plunge — three turns of the circuit, written for the Saharian climate.",
          fr: "Jacuzzi chaud, sauna sec, immersion fraîche — trois tours du circuit, pensés pour le climat saharien.",
          es: "Jacuzzi caliente, sauna seca, baño fresco — tres vueltas al circuito, pensadas para el clima sahariano.",
        },
      },
      {
        title: { en: "Rose & ghassoul facial", fr: "Soin du visage rose & ghassoul", es: "Facial rosa y ghassoul" },
        desc: {
          en: "Atlas rose hydrosol cleanse, ghassoul-clay mask, argan finish — for skin tired of dune wind.",
          fr: "Nettoyage à l'hydrolat de rose de l'Atlas, masque au ghassoul, finition à l'argan — pour les peaux fatiguées du vent.",
          es: "Limpieza con hidrolato de rosa del Atlas, mascarilla de ghassoul, finalización con argán — para pieles cansadas del viento.",
        },
      },
    ],
    spaces: [
      {
        name: { en: "Saharian hammam", fr: "Hammam saharien", es: "Hammam sahariano" },
        detail: {
          en: "Tadelakt walls in dune ochre, heated stone benches, eucalyptus steam.",
          fr: "Murs en tadelakt ocre des dunes, bancs en pierre chauffés, vapeur d'eucalyptus.",
          es: "Paredes en tadelakt ocre de las dunas, bancos de piedra calefactados, vapor de eucalipto.",
        },
      },
      {
        name: { en: "Jacuzzi & sauna", fr: "Jacuzzi & sauna", es: "Jacuzzi y sauna" },
        detail: {
          en: "A hot Jacuzzi facing a small interior garden and an adjacent dry Finnish sauna.",
          fr: "Un Jacuzzi chaud face à un petit jardin intérieur et un sauna finlandais sec attenant.",
          es: "Un Jacuzzi caliente frente a un pequeño jardín interior y una sauna finlandesa contigua.",
        },
      },
      {
        name: { en: "Tisanerie", fr: "Tisanerie", es: "Tisanería" },
        detail: {
          en: "Mint tea, hibiscus and verbena infusions served between treatments — slow conversation only.",
          fr: "Thé à la menthe, infusions d'hibiscus et de verveine entre les soins — conversation lente uniquement.",
          es: "Té con menta, infusiones de hibisco y verbena entre tratamientos — solo conversación lenta.",
        },
      },
    ],
    cta_to: "/contact?topic=spa-tombouctou",
  },

  // ───────────────────────────────────────────────────────────────────────────
  "hotel-riad-xaluca": {
    image:
      "https://images.unsplash.com/photo-1583416750470-965b2707b355?auto=format&fit=crop&w=2000&q=80",
    signature_label: {
      en: "Boutique riad spa",
      fr: "Spa de riad boutique",
      es: "Spa de riad boutique",
    },
    title: {
      en: "An intimate spa,",
      fr: "Un spa intime,",
      es: "Un spa íntimo,",
    },
    title_accent: {
      en: "set inside the riad.",
      fr: "au cœur du riad.",
      es: "en el corazón del riad.",
    },
    intro: {
      en: "The riad's spa is small by design — two private cabins, one steam hammam, candles and the smell of orange blossom from the patio. Treatments are written for the calm of a private home, not the volume of a hotel. Book the entire space for a couple — most guests do.",
      fr: "Le spa du riad est petit par choix — deux cabines privatives, un hammam à vapeur, des bougies et le parfum de la fleur d'oranger venu du patio. Les soins sont pensés pour le calme d'une maison privée, pas pour le volume d'un hôtel. Réservez l'ensemble en duo — la plupart de nos hôtes le font.",
      es: "El spa del riad es pequeño por elección — dos cabinas privadas, un hammam de vapor, velas y el aroma del azahar que llega del patio. Los tratamientos están pensados para la calma de una casa privada, no para el volumen de un hotel. Reserve todo el espacio en pareja — la mayoría de los huéspedes lo hace.",
    },
    stats: [
      { value: { en: "2", fr: "2", es: "2" }, label: { en: "Private cabins", fr: "Cabines privatives", es: "Cabinas privadas" } },
      { value: { en: "1", fr: "1", es: "1" }, label: { en: "Steam hammam", fr: "Hammam à vapeur", es: "Hammam de vapor" } },
      { value: { en: "Exclusive", fr: "Exclusif", es: "Exclusivo" }, label: { en: "Private buy-out", fr: "Privatisation", es: "Privatización" } },
    ],
    experiences: [
      {
        title: { en: "Riad hammam à deux", fr: "Hammam du riad à deux", es: "Hammam del riad para dos" },
        desc: {
          en: "Steam, beldi-soap scrub and rhassoul wrap for two — the spa privatized for one hour and a half.",
          fr: "Vapeur, gommage au savon beldi et enveloppement au rhassoul à deux — le spa privatisé une heure trente.",
          es: "Vapor, exfoliación con jabón beldi y envoltura de rhassoul para dos — el spa privatizado durante una hora y media.",
        },
      },
      {
        title: { en: "Orange-blossom facial", fr: "Soin du visage fleur d'oranger", es: "Facial azahar" },
        desc: {
          en: "Hydrating ritual with neroli, orange blossom and argan — calm skin, calm patio.",
          fr: "Rituel hydratant au néroli, fleur d'oranger et argan — peau apaisée, patio calme.",
          es: "Ritual hidratante con neroli, azahar y argán — piel calmada, patio en calma.",
        },
      },
      {
        title: { en: "Sand-warm stone massage", fr: "Massage aux pierres chaudes du sable", es: "Masaje con piedras calientes" },
        desc: {
          en: "Volcanic stones heated to sand temperature glide along the meridians.",
          fr: "Pierres volcaniques chauffées à la température du sable, le long des méridiens.",
          es: "Piedras volcánicas calentadas a la temperatura de la arena, a lo largo de los meridianos.",
        },
      },
    ],
    spaces: [
      {
        name: { en: "Tadelakt hammam", fr: "Hammam tadelakt", es: "Hammam tadelakt" },
        detail: {
          en: "Olive-oil polished walls, one heated bench, the smallest hammam in the collection.",
          fr: "Murs polis à l'huile d'olive, une banquette chauffée, le plus petit hammam de la collection.",
          es: "Paredes pulidas con aceite de oliva, un banco calefactado, el hammam más pequeño de la colección.",
        },
      },
      {
        name: { en: "Candle-lit cabins", fr: "Cabines aux chandelles", es: "Cabinas a la luz de las velas" },
        detail: {
          en: "Two cabins side by side, designed for couples' or four-hand treatments.",
          fr: "Deux cabines côte à côte, pensées pour le duo ou les soins à quatre mains.",
          es: "Dos cabinas una al lado de la otra, pensadas para parejas o tratamientos a cuatro manos.",
        },
      },
      {
        name: { en: "Patio relaxation", fr: "Relaxation au patio", es: "Relajación en el patio" },
        detail: {
          en: "Linen sofas under the orange tree — mint tea and almond cookies between treatments.",
          fr: "Canapés en lin sous l'oranger — thé à la menthe et cornes de gazelle entre les soins.",
          es: "Sofás de lino bajo el naranjo — té con menta y cuernos de gacela entre tratamientos.",
        },
      },
    ],
    cta_to: "/contact?topic=spa-riad",
  },

  // ───────────────────────────────────────────────────────────────────────────
  "hotel-xaluca-dades": {
    image:
      "https://images.unsplash.com/photo-1556760544-74068565f05c?auto=format&fit=crop&w=2000&q=80",
    signature_label: {
      en: "Roses-Valley wellness",
      fr: "Bien-être de la vallée des roses",
      es: "Bienestar del valle de las rosas",
    },
    title: {
      en: "Spa of the",
      fr: "Spa de la",
      es: "Spa de la",
    },
    title_accent: {
      en: "Roses Valley.",
      fr: "vallée des Roses.",
      es: "valle de las Rosas.",
    },
    intro: {
      en: "Above the Dadès, the spa borrows from the surrounding Roses Valley — petals from M'Goun, ghassoul clay from the Atlas, argan from the south. The cabins look onto the gorges; the relaxation lounge faces a fireplace that burns from October to May.",
      fr: "Au-dessus du Dadès, le spa puise dans la vallée des Roses qui l'entoure — pétales du M'Goun, ghassoul de l'Atlas, argan du sud. Les cabines ouvrent sur les gorges ; le salon de relaxation fait face à une cheminée qui brûle d'octobre à mai.",
      es: "Sobre el Dadès, el spa toma de la valle de las Rosas que lo rodea — pétalos del M'Goun, ghassoul del Atlas, argán del sur. Las cabinas se abren a las gargantas; el salón de relajación tiene una chimenea que arde de octubre a mayo.",
    },
    stats: [
      { value: { en: "3", fr: "3", es: "3" }, label: { en: "Treatment cabins", fr: "Cabines de soin", es: "Cabinas de tratamiento" } },
      { value: { en: "Rose", fr: "Rose", es: "Rosa" }, label: { en: "Signature ritual", fr: "Rituel signature", es: "Ritual insignia" } },
      { value: { en: "Gorges", fr: "Gorges", es: "Gargantas" }, label: { en: "Cabin views", fr: "Vue des cabines", es: "Vista cabinas" } },
    ],
    experiences: [
      {
        title: { en: "Roses Valley hammam", fr: "Hammam de la vallée des Roses", es: "Hammam del valle de las Rosas" },
        desc: {
          en: "Beldi-soap scrub finished with M'Goun rose petals — the only hammam in the south with this signature.",
          fr: "Gommage au savon beldi finalisé aux pétales de rose du M'Goun — l'unique hammam du sud avec cette signature.",
          es: "Exfoliación con jabón beldi rematada con pétalos de rosa del M'Goun — el único hammam del sur con esta firma.",
        },
      },
      {
        title: { en: "Atlas stone & argan", fr: "Pierre de l'Atlas & argan", es: "Piedra del Atlas y argán" },
        desc: {
          en: "Volcanic stones from the Atlas heated to body temperature, slow argan-oil glide.",
          fr: "Pierres volcaniques de l'Atlas chauffées à la température du corps, glissé lent à l'argan.",
          es: "Piedras volcánicas del Atlas calentadas a la temperatura del cuerpo, deslizamiento lento con argán.",
        },
      },
      {
        title: { en: "Saffron-honey wrap", fr: "Enveloppement safran-miel", es: "Envoltura azafrán-miel" },
        desc: {
          en: "Atlas honey and Talouine saffron envelopment for deep hydration after a day in the gorges.",
          fr: "Enveloppement au miel de l'Atlas et safran de Talouine pour une hydratation profonde après les gorges.",
          es: "Envoltura con miel del Atlas y azafrán de Talouine para hidratación profunda tras las gargantas.",
        },
      },
    ],
    spaces: [
      {
        name: { en: "Stone hammam", fr: "Hammam en pierre", es: "Hammam de piedra" },
        detail: {
          en: "Local stone, two heated benches and a vaulted ceiling that holds the steam.",
          fr: "Pierre locale, deux banquettes chauffées et une voûte qui retient la vapeur.",
          es: "Piedra local, dos bancos calefactados y una bóveda que retiene el vapor.",
        },
      },
      {
        name: { en: "Heated indoor pool", fr: "Piscine intérieure chauffée", es: "Piscina interior climatizada" },
        detail: {
          en: "Year-round heated pool with views onto the gorges — long lanes for slow swimmers.",
          fr: "Piscine chauffée toute l'année avec vue sur les gorges — longs couloirs pour nageurs lents.",
          es: "Piscina climatizada todo el año con vistas a las gargantas — calles largas para nadadores lentos.",
        },
      },
      {
        name: { en: "Fireplace lounge", fr: "Salon à cheminée", es: "Salón con chimenea" },
        detail: {
          en: "Sheepskin chairs around a stone fireplace — almond milk and date pastries after treatments.",
          fr: "Fauteuils en peau de mouton autour d'une cheminée en pierre — lait d'amande et pâtisseries aux dattes après les soins.",
          es: "Sillones de piel de oveja alrededor de una chimenea de piedra — leche de almendra y pastas de dátil tras los tratamientos.",
        },
      },
    ],
    cta_to: "/contact?topic=spa-dades",
  },

  // ───────────────────────────────────────────────────────────────────────────
  "hotel-xaluca-spa-aguelmame-sidi-ali": {
    image:
      "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=2000&q=80",
    signature_label: {
      en: "Lakeside wellness · 2 080 m",
      fr: "Bien-être au bord du lac · 2 080 m",
      es: "Bienestar junto al lago · 2 080 m",
    },
    title: {
      en: "A lake spa",
      fr: "Un spa au bord du lac",
      es: "Un spa junto al lago",
    },
    title_accent: {
      en: "at 2,080 m.",
      fr: "à 2 080 m.",
      es: "a 2 080 m.",
    },
    intro: {
      en: "The highest spa in the Xaluca collection — a heated indoor pool with integrated balneo, a Finnish sauna and a steam hammam, all opening onto Lake Aguelmame Sidi Ali. Designed for slow days at altitude: a swim, a sauna, a long cedar-oil massage by the window.",
      fr: "Le spa le plus haut de la collection Xaluca — une piscine intérieure chauffée avec espace balnéo intégré, un sauna finlandais et un hammam à vapeur, le tout ouvert sur le lac Aguelmame Sidi Ali. Pensé pour les journées lentes en altitude : un bain, un sauna, un long massage à l'huile de cèdre près de la fenêtre.",
      es: "El spa más alto de la colección Xaluca — una piscina interior climatizada con espacio balneo integrado, una sauna finlandesa y un hammam de vapor, todo abierto al lago Aguelmame Sidi Ali. Pensado para días lentos en altitud: un baño, una sauna, un largo masaje con aceite de cedro junto a la ventana.",
    },
    stats: [
      { value: { en: "2 080 m", fr: "2 080 m", es: "2 080 m" }, label: { en: "Altitude", fr: "Altitude", es: "Altitud" } },
      { value: { en: "3", fr: "3", es: "3" }, label: { en: "Treatment cabins", fr: "Cabines de soin", es: "Cabinas de tratamiento" } },
      { value: { en: "Lake", fr: "Lac", es: "Lago" }, label: { en: "Spa views", fr: "Vue du spa", es: "Vista del spa" } },
    ],
    experiences: [
      {
        title: { en: "Cedar-oil ritual", fr: "Rituel à l'huile de cèdre", es: "Ritual de aceite de cedro" },
        desc: {
          en: "Steam, scrub and Atlas-cedar oil massage — the signature ritual of the lake.",
          fr: "Vapeur, gommage et massage à l'huile de cèdre de l'Atlas — le rituel signature du lac.",
          es: "Vapor, exfoliación y masaje con aceite de cedro del Atlas — el ritual insignia del lago.",
        },
      },
      {
        title: { en: "Altitude-balance breathing", fr: "Respiration d'équilibre altitude", es: "Respiración de equilibrio en altitud" },
        desc: {
          en: "A short guided breathing session in the heated pool, ideal on day-one to settle into 2,080 m.",
          fr: "Une courte séance de respiration guidée dans la piscine chauffée, idéale dès l'arrivée à 2 080 m.",
          es: "Una breve sesión de respiración guiada en la piscina climatizada, ideal el primer día a 2 080 m.",
        },
      },
      {
        title: { en: "Snow & sauna circuit", fr: "Circuit neige & sauna", es: "Circuito nieve y sauna" },
        desc: {
          en: "In winter — hot sauna, plunge into the cold lake water, repeat. A Berber-Nordic exchange.",
          fr: "En hiver — sauna chaud, immersion dans l'eau froide du lac, on recommence. Un échange berbéro-nordique.",
          es: "En invierno — sauna caliente, inmersión en el agua fría del lago, repetir. Un intercambio bereber-nórdico.",
        },
      },
    ],
    spaces: [
      {
        name: { en: "Heated indoor pool", fr: "Piscine intérieure chauffée", es: "Piscina interior climatizada" },
        detail: {
          en: "Year-round 32 °C pool with integrated balneo jets, lake-facing window wall.",
          fr: "Piscine 32 °C toute l'année, jets balnéo intégrés, mur de fenêtres face au lac.",
          es: "Piscina 32 °C todo el año, chorros balneo integrados, pared de ventanas frente al lago.",
        },
      },
      {
        name: { en: "Finnish sauna", fr: "Sauna finlandais", es: "Sauna finlandesa" },
        detail: {
          en: "Dry cedar cabin at 80 °C — eucalyptus and atlas-cedar infusions.",
          fr: "Cabine sèche en cèdre à 80 °C — infusions d'eucalyptus et de cèdre de l'Atlas.",
          es: "Cabina seca de cedro a 80 °C — infusiones de eucalipto y cedro del Atlas.",
        },
      },
      {
        name: { en: "Steam hammam & two cabins", fr: "Hammam à vapeur & deux cabines", es: "Hammam de vapor y dos cabinas" },
        detail: {
          en: "Steam hammam plus two individual cabins and one duo cabin, with a small fitness annex.",
          fr: "Hammam à vapeur, deux cabines individuelles et une cabine duo, avec une petite salle de fitness attenante.",
          es: "Hammam de vapor, dos cabinas individuales y una cabina dúo, con una pequeña sala de fitness contigua.",
        },
      },
    ],
    cta_to: "/contact?topic=spa-sidi-ali",
  },
};

export const getSpaFeature = (slug) => SPA_FEATURES[slug] || null;
